import { useRef, useState } from 'react'
import { Button, TextField } from './ui'

interface Props {
  id: string
  value: string
  onChange: (value: string) => void
  disabled?: boolean
  autoFocus?: boolean
  // `| undefined` spelled out (not just `?:`) because callers pass a conditional expression that
  // can itself evaluate to `undefined` (`cond ? x : undefined`) — under this project's
  // `exactOptionalPropertyTypes`, a bare `?: string` forbids assigning `undefined` explicitly.
  ariaDescribedBy?: string | undefined
  ariaInvalid?: boolean | undefined
}

/**
 * F-040 / REQ-032-002. Codice TOTP o codice di recupero, come alternativa ESPLICITA (un pulsante
 * che cambia modalità, non un campo unico che prova a indovinare cosa hai digitato) — il brief lo
 * chiede esplicitamente: il backend (`MfaService.TryVerifyCodeAsync`) accetta entrambi nello stesso
 * campo `code`, ma l'interfaccia deve comunque distinguerli, con attributi diversi (`inputMode`,
 * `autoComplete`, lunghezza) adatti a ciascuno — un codice a 6 cifre chiede la tastiera numerica e
 * l'autocompilazione "one-time-code" dei password manager, un codice di recupero (alfanumerico, coi
 * trattini) non deve forzare la tastiera numerica su mobile.
 */
export function MfaCodeInput({ id, value, onChange, disabled, autoFocus, ariaDescribedBy, ariaInvalid }: Props) {
  const [mode, setMode] = useState<'totp' | 'recovery'>('totp')
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null)

  function switchMode(next: 'totp' | 'recovery') {
    setMode(next)
    onChange('')
    // Il campo cambia sotto lo stesso label/id: sposta di nuovo il focus lì, non lasciarlo sul
    // pulsante che ha appena cambiato modalità.
    requestAnimationFrame(() => inputRef.current?.focus())
  }

  return (
    <div>
      {mode === 'totp' ? (
        <TextField
          key="totp"
          label="Codice a 6 cifre"
          id={id}
          ref={inputRef}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="[0-9]{6}"
          maxLength={6}
          supportingText="Esempio: 123456"
          required
          autoFocus={autoFocus}
          disabled={disabled}
          value={value}
          onChange={e => onChange(e.target.value.replace(/\D/g, '').slice(0, 6))}
          aria-describedby={ariaDescribedBy}
          error={ariaInvalid ? 'Controlla il codice inserito' : undefined}
        />
      ) : (
        <TextField
          key="recovery"
          label="Codice di recupero"
          id={id}
          ref={inputRef}
          type="text"
          inputMode="text"
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          supportingText="Formato: XXXX-XXXX-XXXX-XXXX"
          required
          autoFocus={autoFocus}
          disabled={disabled}
          value={value}
          onChange={e => onChange(e.target.value)}
          aria-describedby={ariaDescribedBy}
          error={ariaInvalid ? 'Controlla il codice inserito' : undefined}
        />
      )}
      <Button
        variant="text"
        size="sm"
        className="mt-1"
        onClick={() => switchMode(mode === 'totp' ? 'recovery' : 'totp')}
        disabled={disabled}
      >
        {mode === 'totp' ? 'Non hai accesso all’app? Usa un codice di recupero' : 'Usa invece il codice a 6 cifre'}
      </Button>
    </div>
  )
}
